import React from 'react';

import { getStaticsBaseUrl } from '@oysterjs/core/config';
import { ensureBrandWebFonts } from '~/components/brand-web-fonts';

import { scopeCSS } from '~/components/document-editor/editor-css';
import { parseFullHTML } from '~/components/document-editor/template';

const STYLESHEET_ATTEMPTS = 2;
const STYLESHEET_TIMEOUT_MS = 10_000;
const INITIAL_PAINT_DELAY_MS = 1_500;

export const STYLESHEET_FAILURE_NOTICE =
  'Document styles could not be loaded. Colors and fonts may differ from the PDF.';

const fetchStylesheet = async (url: string, cancel: AbortSignal): Promise<string> => {
  const controller = new AbortController();
  const abort = () => controller.abort();
  cancel.addEventListener('abort', abort);
  const timer = setTimeout(abort, STYLESHEET_TIMEOUT_MS);
  try {
    const response = await fetch(url, { credentials: 'include', signal: controller.signal });
    if (!response.ok) {
      throw new Error(`stylesheet responded ${response.status}`);
    }
    return await response.text();
  } finally {
    clearTimeout(timer);
    cancel.removeEventListener('abort', abort);
  }
};

const fetchStylesheetWithRetry = async (url: string, cancel: AbortSignal): Promise<string> => {
  let lastError: unknown;
  for (let attempt = 0; attempt < STYLESHEET_ATTEMPTS && !cancel.aborted; attempt++) {
    try {
      return await fetchStylesheet(url, cancel);
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError;
};

/**
 * A workspace artifact is styled by its own route. A Library template being edited has no
 * artifact, so it is styled by the template's route, which carries no version or revision.
 */
const stylesheetUrl = (params: {
  workspaceId?: string;
  artifactId?: string;
  artifactVersion?: number;
  artifactRevision?: string | number;
  templateId?: string;
  templateDesignId?: string | null;
}): string | null => {
  const {
    workspaceId,
    artifactId,
    artifactVersion,
    artifactRevision,
    templateId,
    templateDesignId
  } = params;
  if (workspaceId && artifactId) {
    const urlParams = new URLSearchParams();
    if (templateId) urlParams.set('style_id', templateId);
    if (artifactVersion !== undefined) urlParams.set('version', String(artifactVersion));
    if (artifactRevision !== undefined) urlParams.set('render_revision', String(artifactRevision));
    const qs = urlParams.toString() ? `?${urlParams.toString()}` : '';
    return `${getStaticsBaseUrl()}/agency/html/${workspaceId}/${artifactId}/stylesheet${qs}`;
  }
  if (templateId && !workspaceId && !artifactId) {
    const query = new URLSearchParams();
    if (templateDesignId !== undefined) query.set('design', templateDesignId ?? 'default');
    const suffix = query.size ? `?${query.toString()}` : '';
    return `${getStaticsBaseUrl()}/agency/html-template/${templateId}/stylesheet${suffix}`;
  }
  return null;
};

const failureNotice = (): HTMLElement => {
  const notice = document.createElement('div');
  notice.setAttribute('data-editor-stylesheet-notice', 'true');
  notice.setAttribute('role', 'status');
  notice.className =
    'mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800';
  notice.textContent = STYLESHEET_FAILURE_NOTICE;
  return notice;
};

/**
 * Inject scoped CSS into the editor root: document `<head>` styles first, then
 * the backend stylesheet (Styles tab / statics.go) so preview matches PDF order.
 *
 * The first paint waits briefly for backend styles, then shows the document with a notice
 * while loading continues. Refreshes keep the last stylesheet until its replacement arrives.
 */
export function useEditorStylesheets(params: {
  editorRootRef: React.RefObject<HTMLDivElement | null>;
  content: string;
  scopeId: string;
  workspaceId?: string;
  artifactId?: string;
  artifactVersion?: number;
  artifactRevision?: string | number;
  templateId?: string;
  templateDesignId?: string | null;
  showPreview?: boolean;
}) {
  const {
    editorRootRef,
    content,
    scopeId,
    workspaceId,
    artifactId,
    artifactVersion,
    artifactRevision,
    templateId,
    templateDesignId,
    showPreview
  } = params;

  const backendStyleRef = React.useRef<HTMLStyleElement | null>(null);
  React.useEffect(
    () => () => {
      backendStyleRef.current?.remove();
      backendStyleRef.current = null;
    },
    []
  );

  // The catalog's faces live on the render pod and must also be loaded in the editor.
  React.useEffect(() => {
    ensureBrandWebFonts();
  }, []);

  React.useEffect(() => {
    const root = editorRootRef.current;
    if (!root) return;
    const parsed = parseFullHTML(content);
    const headStyles = parsed?.headStyles ?? '';
    if (!headStyles.trim()) return;
    const styleEl = document.createElement('style');
    styleEl.setAttribute('data-editor-head-styles', 'true');
    styleEl.textContent = scopeCSS(headStyles, scopeId, true);
    root.prepend(styleEl);
    return () => styleEl.remove();
  }, [content, artifactId, artifactVersion, artifactRevision, editorRootRef, scopeId, showPreview]);

  React.useEffect(() => {
    const root = editorRootRef.current;
    if (!root) return;

    const url = stylesheetUrl({
      workspaceId,
      artifactId,
      artifactVersion,
      artifactRevision,
      templateId,
      templateDesignId
    });
    if (!url) return;

    const controller = new AbortController();
    const styleEl = document.createElement('style');
    styleEl.setAttribute('data-editor-backend-styles', 'true');
    const notice = failureNotice();

    const firstPaint = backendStyleRef.current?.parentElement !== root;
    if (firstPaint) root.style.visibility = 'hidden';
    const paintTimer = firstPaint
      ? setTimeout(() => {
          root.style.visibility = '';
          notice.textContent = 'Loading document styles. Colors and fonts may change.';
          root.prepend(notice);
        }, INITIAL_PAINT_DELAY_MS)
      : undefined;

    fetchStylesheetWithRetry(url, controller.signal)
      .then((css) => {
        if (controller.signal.aborted) return;
        const inner = css.replace(/<\/?style[^>]*>/gi, '').trim();
        if (!inner) return;
        styleEl.textContent = scopeCSS(inner, scopeId, true, true);
        root.appendChild(styleEl);
        backendStyleRef.current?.remove();
        backendStyleRef.current = styleEl;
        notice.remove();
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          notice.textContent = STYLESHEET_FAILURE_NOTICE;
          root.prepend(notice);
        }
      })
      .finally(() => {
        clearTimeout(paintTimer);
        if (!controller.signal.aborted) root.style.visibility = '';
      });

    return () => {
      controller.abort();
      clearTimeout(paintTimer);
      root.style.visibility = '';
      notice.remove();
    };
  }, [
    artifactId,
    artifactVersion,
    artifactRevision,
    editorRootRef,
    scopeId,
    showPreview,
    templateId,
    templateDesignId,
    workspaceId
  ]);
}
